/**
 * Debounce utility function (mainly for search)
 * Prevents excessive API calls by waiting for user to stop typing
 *
 * @param {Function} func - The function to debounce
 * @param {number} delay - Delay in milliseconds (default: 300ms)
 * @returns {Function} - Debounced function
 */
export const debounce = (func, delay = 300) => {
    let timeoutId;

    return function (...args) {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }

        timeoutId = setTimeout(() => {
            func.apply(this, args);
        }, delay);
    };
};

/**
 * Debounce with immediate first call (useful for initial empty state)
 *
 * @param {Function} func - The function to debounce
 * @param {number} delay - Delay in milliseconds (default: 300ms)
 * @returns {Function} - Debounced function with immediate first call
 */
export const debounceImmediate = (func, delay = 300) => {
    let timeoutId;
    let isFirstCall = true;

    return function (...args) {
        const callNow = isFirstCall;
        isFirstCall = false;

        // Clear any existing timeout
        if (timeoutId) {
            clearTimeout(timeoutId);
        }

        // Set new timeout
        timeoutId = setTimeout(() => {
            isFirstCall = true;
            func.apply(this, args);
        }, delay);

        // Call immediately on first invocation
        if (callNow) {
            func.apply(this, args);
        }
    };
};

/**
 * Throttle utility function
 * Limits function calls to once per delay period (useful for loading states)
 *
 * @param {Function} func - The function to throttle
 * @param {number} delay - Delay in milliseconds (default: 1000ms)
 * @returns {Function} - Throttled function
 */
export const throttle = (func, delay = 1000) => {
    let lastCall = 0;

    return function (...args) {
        const now = Date.now();

        if (now - lastCall >= delay) {
            lastCall = now;
            func.apply(this, args);
        }
    };
};